import { useId, useState } from 'react'

import { CATEGORIES } from './estimate.js'

// Adding something to the board.
//
// *"The 1am path stays two fields"* — a category and a title is a complete
// entry, and everything else on this form is optional. That is the spec's
// wording and it is a constraint on this component: anything that makes the
// short path longer is the wrong trade, because the moment this exists for is
// somebody on a call pasting a link before the conversation moves on.
//
// ⚠️ THE TITLE IS STILL TYPED, AND THE FORM STILL SAYS SO. P6-82 made the
// Function that creates the entry ask the service for a preview — behind the
// SSRF guard matrix, in the service, where T8 put it — but that request is
// registered work that outlives this response, and its result arrives on the
// changes feed some seconds later. Nothing about it is awaited here.
//
// So this form must never start waiting on a photo, and the hint on the link
// field says what actually happens rather than promising one: a page with no
// OpenGraph tags, and a refusal, are both ordinary outcomes and both leave the
// card exactly as the member typed it.
//
// The cost is deliberately three inputs rather than one clever one. A single
// "£1840 flat" box needs a parser, and a parser is a thing that decides what
// somebody meant — which is the failure this product is named after. The
// amount is sent as the string that was typed and the service decides whether
// it is a number it can store.

const BASES = [
  { value: '', label: 'default for this category' },
  { value: 'per_person', label: 'per person' },
  { value: 'flat', label: 'flat, split between everyone' },
]

export default function AddEntry({ onAdd, busy = false }) {
  const formId = useId()
  const [open, setOpen] = useState(false)
  const [category, setCategory] = useState('stay')
  const [title, setTitle] = useState('')
  const [url, setUrl] = useState('')
  const [amount, setAmount] = useState('')
  const [currency, setCurrency] = useState('')
  const [basis, setBasis] = useState('')
  const [costNote, setCostNote] = useState('')

  const reset = () => {
    setTitle('')
    setUrl('')
    setAmount('')
    setCurrency('')
    setBasis('')
    setCostNote('')
  }

  const submit = async (event) => {
    event.preventDefault()
    if (title.trim() === '') return

    const body = { category, title: title.trim() }
    if (url.trim() !== '') body.url = url.trim()
    if (costNote.trim() !== '') body.cost_note = costNote.trim()
    // 🔴 `cost` is sent only when an amount was actually typed. An empty
    // string here would become a cost triple the crew did not type, and "a cost
    // the crew did not type must not appear as a figure" is the rule the whole
    // schema is arranged around.
    if (amount.trim() !== '') {
      body.cost = { amount: amount.trim(), currency: currency.trim().toUpperCase() }
      if (basis !== '') body.cost.basis = basis
    }

    const ok = await onAdd?.(body)
    if (ok) {
      reset()
      setOpen(false)
    }
  }

  if (!open) {
    return (
      <div className="d2d-add">
        <button type="button" className="d2d-add__open" onClick={() => setOpen(true)}>
          Add something to the board
        </button>
      </div>
    )
  }

  return (
    <form className="d2d-add d2d-add--open" onSubmit={submit} aria-label="Add something">
      <div className="d2d-add__grid">
        <p className="d2d-add__field">
          <label className="d2d-micro" htmlFor={`${formId}-category`}>
            Category
          </label>
          <select
            id={`${formId}-category`}
            value={category}
            onChange={(event) => setCategory(event.target.value)}
          >
            {CATEGORIES.map((entry) => (
              <option key={entry.key} value={entry.key}>
                {entry.label}
              </option>
            ))}
          </select>
        </p>

        <p className="d2d-add__field d2d-add__field--wide">
          <label className="d2d-micro" htmlFor={`${formId}-title`}>
            What is it
          </label>
          <input
            id={`${formId}-title`}
            value={title}
            required
            maxLength={300}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Casa do Rio — whole house, pool"
          />
        </p>

        <p className="d2d-add__field d2d-add__field--wide">
          <label className="d2d-micro" htmlFor={`${formId}-url`}>
            Link (optional)
          </label>
          <input
            id={`${formId}-url`}
            type="url"
            value={url}
            maxLength={2000}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://…"
          />
          <span className="d2d-add__hint">
            We store the link and look for a photo. One shows up on the card if the page
            has one.
          </span>
        </p>

        <p className="d2d-add__field">
          <label className="d2d-micro" htmlFor={`${formId}-amount`}>
            Amount (optional)
          </label>
          <input
            id={`${formId}-amount`}
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="1840"
          />
        </p>

        <p className="d2d-add__field">
          <label className="d2d-micro" htmlFor={`${formId}-currency`}>
            Currency
          </label>
          <input
            id={`${formId}-currency`}
            value={currency}
            maxLength={3}
            onChange={(event) => setCurrency(event.target.value)}
            placeholder="GBP"
            required={amount.trim() !== ''}
          />
        </p>

        <p className="d2d-add__field">
          <label className="d2d-micro" htmlFor={`${formId}-basis`}>
            Per person or flat
          </label>
          <select
            id={`${formId}-basis`}
            value={basis}
            onChange={(event) => setBasis(event.target.value)}
          >
            {BASES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </p>

        <p className="d2d-add__field d2d-add__field--wide">
          <label className="d2d-micro" htmlFor={`${formId}-note`}>
            A note about the cost (optional)
          </label>
          <input
            id={`${formId}-note`}
            value={costNote}
            maxLength={300}
            onChange={(event) => setCostNote(event.target.value)}
            placeholder="£300ish each, the host said on the phone"
          />
          {/* Says what the note is NOT, because the alternative is somebody
              typing a price in here and wondering why the estimate ignored it.
              The service excludes it deliberately; this is that exclusion,
              said in advance. */}
          <span className="d2d-add__hint">
            A note is words, not an amount — it stays off the estimate.
          </span>
        </p>
      </div>

      <div className="d2d-add__actions">
        <button type="submit" className="d2d-add__submit" disabled={busy}>
          {busy ? 'Adding…' : 'Add it'}
        </button>
        <button
          type="button"
          className="d2d-add__cancel"
          onClick={() => {
            reset()
            setOpen(false)
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
